import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ArrowDown, Hash, Lock, MessageSquare, MousePointerClick, User, Users } from "lucide-react";
import type { LucideIcon } from "lucide-react";

import type { ApiConversation, ApiMessage, ApiUser } from "../lib/api";
import { tsDayKey } from "../lib/format";
import { isGroupedContinuation } from "../lib/grouping";
import {
  buildAvatarPaletteByUserID,
  useMessagesPage,
} from "../lib/hooks";
import { useAppState } from "../state/AppContext";
import { DateSeparator, Message } from "./Message";
import { ResizeHandle } from "./ResizeHandle";
import { EmptyState } from "./ui/EmptyState";
import { Tooltip } from "./ui/Tooltip";
import { ThreadDrawer } from "./ThreadDrawer";
import { THREAD_BOUNDS } from "../lib/useLayoutState";

type ConversationType = ApiConversation["type"];

const TYPE_ICON: Record<ConversationType, LucideIcon> = {
  public_channel: Hash,
  private_channel: Lock,
  mpim: Users,
  im: User,
};

interface MessagePaneProps {
  teamID: string;
  conversation: ApiConversation | null;
  /** Workspace users dict — passed in by parent so the hook
   *  fetch is shared across the whole authenticated view rather
   *  than refetched per conversation switch. */
  users: Record<string, ApiUser>;
  /** All conversations in the workspace — needed for in-text
   *  #channel mention rendering. */
  conversations: ApiConversation[];
  /** Pass 2.12: pixel-native width passed down from BrowseView's
   *  useLayoutState. Below the viewport threshold (900px),
   *  BrowseView passes the spec default (420). The drawer is
   *  sized via inline style — no Tailwind w-* class. */
  threadDrawerWidth: number;
  /** Pass 2.12: optional onResize for the drawer. When defined,
   *  MessagePane renders a ResizeHandle between the body and the
   *  drawer; when undefined (narrow viewport), the drawer is
   *  rendered without a handle. */
  onResizeThreadDrawer?: (width: number) => void;
  /** Pass 2.13: the ts of a message the search UI is jumping to.
   *  When set, MessagePane (a) seeds useMessagesPage with this
   *  ts via the around-fetch path, (b) scrolls to the matching
   *  row after paint, (c) applies a transient highlight class
   *  for ~1.5s. Re-pointing to a different ts re-runs the effect.
   *  Null in normal browse mode. */
  targetMessageTs?: string | null;
  /** S3.7: invoked when the user clicks "jump to latest" — the
   *  parent clears any ?ts param from the URL. The scroll-to-bottom
   *  itself is handled locally. Optional (router-free in tests). */
  onJumpToLatest?: () => void;
  /** S4.0: jump to + highlight a thread reply's parent. Parent
   *  delegates to the scroll-to-ts machinery (navigate ?ts=). */
  onGoToParent?: (parentTs: string) => void;
}

type RenderItem =
  | { kind: "date"; key: string; ts: string }
  | {
      kind: "msg";
      key: string;
      msg: ApiMessage;
      groupedContinuation: boolean;
    };

function buildItems(messages: ApiMessage[]): RenderItem[] {
  const out: RenderItem[] = [];
  let lastDay: string | null = null;
  let prev: { user: string | undefined; ts: string } | null = null;
  for (const m of messages) {
    const day = tsDayKey(m.ts);
    if (day !== lastDay) {
      out.push({ kind: "date", key: `d-${m.ts}`, ts: m.ts });
      lastDay = day;
      prev = null;
    }
    const continuation = isGroupedContinuation(prev, {
      user: m.user ?? undefined,
      ts: m.ts,
    });
    out.push({
      kind: "msg",
      key: m.ts,
      msg: m,
      groupedContinuation: continuation,
    });
    prev = { user: m.user ?? undefined, ts: m.ts };
  }
  return out;
}

export function MessagePane({
  teamID,
  conversation,
  users,
  conversations,
  threadDrawerWidth,
  onResizeThreadDrawer,
  targetMessageTs,
  onJumpToLatest,
  onGoToParent,
}: MessagePaneProps) {
  const { openThreadParentTS, setOpenThreadParent } = useAppState();
  const convID = conversation?.id ?? null;
  const {
    messages,
    loading,
    loadingMore,
    error,
    hasMore,
    hasMoreAfter,
    loadMore,
    loadNewer,
    targetTs,
  } = useMessagesPage(teamID, convID, targetMessageTs ?? null);

  // Pass 2.13.4: prop sync is handled inside useMessagesPage now —
  // a useEffect there mirrors initialTargetTs into the hook's
  // internal targetTs state. Pre-2.13.4 this component held a
  // lastTargetRef + effect that called jumpToTs on prop change,
  // which only forwarded non-null values and missed the
  // "drop back to null" case (BUG 4 — sidebar click after search
  // jump kept stale ts and 404'd the next /messages fetch).

  const avatarPaletteByUserID = useMemo(
    () => buildAvatarPaletteByUserID(users),
    [users],
  );

  const items = useMemo(() => buildItems(messages), [messages]);

  // Pass 6.0b.3 — ts → message index so the broadcast header can
  // look up its parent in O(1) per render. Built once per message
  // list mutation, which is the same cadence as ``items``.
  const messagesByTs = useMemo(() => {
    const m = new Map<string, ApiMessage>();
    for (const msg of messages) m.set(msg.ts, msg);
    return m;
  }, [messages]);

  // Pass 2.6 + 2.13 scroll management. Three distinct cases:
  //   (a) initial render of a conversation in DEFAULT mode → scroll
  //       to BOTTOM so the newest message is visible (Slack-native).
  //   (b) "Load older" prepended N messages → preserve the user's
  //       reading position by adjusting scrollTop by the height
  //       delta the new messages contributed at the top.
  //   (c) Pass 2.13: targetTs landed (search jump) → scroll the
  //       matched row into view (centred), apply a transient
  //       highlight class for ~1.5s. Beats (a) when both could
  //       apply because (a) only fires on isInitialPaint AND no
  //       targetTs.
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const isInitialPaint = useRef(true);
  const preLoadOlderScrollHeight = useRef<number | null>(null);
  // Pass 2.13: ts the scroll-to-target effect last consumed. Lets
  // us re-scroll when targetTs changes mid-conversation without
  // re-firing on every re-render.
  const lastScrolledTs = useRef<string | null>(null);

  // Pass 2.13.5 (BUG 1 fix v2): the highlighted ts is React state,
  // NOT a DOM mutation. Pre-2.13.5 we did row.classList.add(...)
  // imperatively in the layout effect. That works briefly — until
  // the around-fetch's response triggers a re-render that unmounts
  // and remounts the matched row, at which point the new DOM node
  // is created without the class and the layout effect's
  // lastScrolledTs guard prevents re-adding it. Verified live via
  // MutationObserver: after a search-jump click, the row was
  // 1) class-added → 2) DOM-removed (with class) at +10ms →
  // 3) DOM-re-added without class at +89ms. The user sees no
  // highlight because the visible state is the un-highlighted
  // re-mount. Driving the highlight through React's render output
  // means the className is computed on every reconcile, so the
  // re-mount carries the highlight class with it.
  const [highlightedTs, setHighlightedTs] = useState<string | null>(null);
  const highlightTimerRef = useRef<number | null>(null);

  useEffect(() => {
    isInitialPaint.current = true;
    preLoadOlderScrollHeight.current = null;
    lastScrolledTs.current = null;
    // Pass 2.13.5: a different conversation means the previous
    // highlight target is no longer relevant. Clear so the old ts
    // doesn't leak into the new conversation's render.
    setHighlightedTs(null);
    if (highlightTimerRef.current !== null) {
      window.clearTimeout(highlightTimerRef.current);
      highlightTimerRef.current = null;
    }
  }, [convID]);

  // Cancel the pending un-highlight timer on unmount so a delayed
  // setHighlightedTs(null) doesn't fire after the component is gone.
  useEffect(
    () => () => {
      if (highlightTimerRef.current !== null) {
        window.clearTimeout(highlightTimerRef.current);
        highlightTimerRef.current = null;
      }
    },
    [],
  );

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el || messages.length === 0) return;

    // (c) Pass 2.13: targetTs scroll-to. Wins over (a) when set —
    // we don't want the initial-paint jump-to-bottom to fight with
    // a search-jump on the same render.
    if (targetTs && lastScrolledTs.current !== targetTs) {
      const row = el.querySelector(
        `[data-testid="message-row"][data-message-ts="${CSS.escape(targetTs)}"]`,
      ) as HTMLElement | null;
      if (row) {
        row.scrollIntoView({ behavior: "smooth", block: "center" });
        // Pass 2.13.5: highlight via React state — the className
        // gets recomputed on every reconcile so the class survives
        // a row remount across the around-fetch resolve. Schedule
        // the un-highlight after 10 s; the CSS transition (1 s)
        // fades it out smoothly.
        setHighlightedTs(targetTs);
        if (highlightTimerRef.current !== null) {
          window.clearTimeout(highlightTimerRef.current);
        }
        highlightTimerRef.current = window.setTimeout(() => {
          setHighlightedTs(null);
          highlightTimerRef.current = null;
        }, 10000);
        lastScrolledTs.current = targetTs;
        isInitialPaint.current = false;
        return;
      }
      // Row not in DOM yet (still loading) — leave the effect to
      // fire again on the next message-list update.
      return;
    }

    if (isInitialPaint.current) {
      // (a) jump to bottom on first render-with-messages — only
      // when not in target-ts mode (targetTs would have handled it
      // above).
      el.scrollTop = el.scrollHeight;
      isInitialPaint.current = false;
      return;
    }
    if (preLoadOlderScrollHeight.current !== null) {
      // (b) restore: keep the previously-top message at the same
      // viewport y by scrolling down by the height the prepended
      // messages added.
      const delta = el.scrollHeight - preLoadOlderScrollHeight.current;
      if (delta > 0) {
        el.scrollTop = el.scrollTop + delta;
      }
      preLoadOlderScrollHeight.current = null;
    }
  }, [messages.length, targetTs]);

  const handleLoadOlder = useCallback(() => {
    const el = scrollRef.current;
    preLoadOlderScrollHeight.current = el ? el.scrollHeight : null;
    loadMore();
  }, [loadMore]);

  const handleLoadNewer = useCallback(() => {
    loadNewer();
  }, [loadNewer]);

  // S3.7: jump-to-latest. Show a floating button when the user has
  // scrolled > 100px above the bottom; click scrolls to the bottom
  // and asks the parent to drop any ?ts permalink param.
  const [showJumpToLatest, setShowJumpToLatest] = useState(false);
  const jumpRafRef = useRef<number | null>(null);

  const onBodyScroll = useCallback(() => {
    if (jumpRafRef.current !== null) return;
    jumpRafRef.current = window.requestAnimationFrame(() => {
      jumpRafRef.current = null;
      const el = scrollRef.current;
      if (!el) return;
      const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      setShowJumpToLatest(distanceFromBottom > 100);
    });
  }, []);

  const jumpToLatest = useCallback(() => {
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    setShowJumpToLatest(false);
    onJumpToLatest?.();
  }, [onJumpToLatest]);

  const threadParent = useMemo<ApiMessage | null>(() => {
    if (!openThreadParentTS) return null;
    return messages.find((m) => m.ts === openThreadParentTS) ?? null;
  }, [messages, openThreadParentTS]);

  if (!conversation) {
    return (
      <main
        data-testid="message-pane"
        data-state="empty"
        className="flex-1 flex items-center justify-center bg-theme-bg-message-pane min-w-0 relative"
      >
        <EmptyState
          icon={<MousePointerClick className="w-full h-full" strokeWidth={1.5} />}
          title="Select a conversation"
          description="Pick a channel or DM from the list to read its messages."
        />
      </main>
    );
  }

  const Icon = TYPE_ICON[conversation.type];
  // Pass 2.13.6: stale-while-revalidate — when a re-fetch is in
  // flight (loading=true) but we ALREADY have messages from the
  // previous fetch, keep showing them instead of unmounting all
  // rows in favour of a "Loading messages..." placeholder. The
  // unmount was breaking the search-jump scroll effect: the layout
  // effect ran the moment loading=true was set, found NO message
  // rows in the DOM (because the JSX conditional rendered the
  // loading placeholder instead), and silently early-returned.
  // When the fetch resolved and rows re-mounted, the effect's
  // [messages.length, targetTs] deps were unchanged so it never
  // re-ran. The 1st click happened to "work" only because its
  // target ts was at the conversation's tail and the body's
  // scrollTop clamped to max; the 2nd click's mid-conversation
  // target landed wherever the post-unmount scroll happened to be
  // (typically the start of the conversation). With the
  // stale-while-revalidate change, the rows stay in the DOM
  // throughout the in-flight window so scrollIntoView always finds
  // the target.
  const bodyState: "loading" | "error" | "empty" | "messages" =
    loading && messages.length === 0
      ? "loading"
      : error
        ? "error"
        : messages.length > 0
          ? "messages"
          : "empty";

  const threadOpen = openThreadParentTS !== null;

  return (
    <main
      data-testid="message-pane"
      data-state="conversation"
      data-conversation-id={conversation.id}
      data-workspace-id={teamID}
      aria-label={`Messages in ${conversation.label}`}
      // Pass 2.12: dropped `relative` (no longer needed — the
      // ThreadDrawer was the only consumer of the relative-positioning
      // anchor and it's no longer absolute). The horizontal flex
      // container below replaces what used to be a vertically-stacked
      // body + absolute drawer overlay.
      className="flex-1 flex flex-col bg-theme-bg-message-pane min-w-0 overflow-hidden"
    >
      <header
        data-testid="message-pane-header"
        className="px-6 py-4 border-b border-theme-border-default flex items-center gap-3 flex-shrink-0"
      >
        <Icon
          className="w-4 h-4 text-theme-text-secondary flex-shrink-0"
          strokeWidth={1.75}
          aria-hidden
        />
        <h2
          className="font-serif text-theme-conv-title font-theme-conv-title text-theme-text-primary tracking-tightish leading-tight truncate"
          title={conversation.label}
        >
          {conversation.label}
        </h2>
        <span
          data-testid="message-pane-type-chip"
          className="px-2 py-0.5 rounded-full bg-white/[0.05] border border-white/[0.09] text-[10px] capitalize text-theme-text-secondary flex-shrink-0"
        >
          {conversation.type.replace(/_/g, " ")}
        </span>
        <div
          data-testid="message-pane-counts"
          className="ml-auto font-mono text-[11px] text-theme-text-tertiary flex-shrink-0"
        >
          {messages.length} message{messages.length === 1 ? "" : "s"}
          {hasMore && " · more available"}
        </div>
      </header>

      {/*
        Pass 2.12: horizontal flex row holding the message body
        and (optionally) the thread drawer. The body uses flex-1
        + min-w-0 so it actually shrinks when the drawer opens
        instead of pushing the drawer offscreen. min-h-0 on the
        flex parent below preserves the body's ability to scroll
        — without it, flex's min-content default would let the
        body grow to fit all messages and break the inner scroll.
      */}
      <div className="flex-1 flex overflow-hidden min-h-0">
        {/* S3.7: relative wrapper so the jump-to-latest button can
            float over the message body (excluding the thread drawer,
            which is a sibling of this wrapper). */}
        <div className="relative flex-1 min-w-0 flex flex-col">
        <div
          ref={scrollRef}
          onScroll={onBodyScroll}
          data-testid="message-pane-body"
          data-state={bodyState}
          className="flex-1 overflow-y-auto scrollbar-thin min-w-0"
        >
        {bodyState === "loading" && (
          <p className="p-8 font-mono text-sm text-theme-text-tertiary">
            Loading messages…
          </p>
        )}
        {bodyState === "error" && (
          <p className="p-8 font-mono text-sm text-theme-error-text">
            Could not load conversation: {error?.message}
          </p>
        )}
        {bodyState === "empty" && (
          <EmptyState
            icon={<MessageSquare className="w-full h-full" strokeWidth={1.5} />}
            title="No messages yet"
            description="This conversation doesn't have any messages."
          />
        )}
        {bodyState === "messages" && (
          <>
            {/*
              Pass 2.6: the "Load older" affordance lives at the TOP
              of the message list (above the oldest currently-loaded
              message), not the bottom. When the service reports
              has_more=false we replace the button with a static
              "Beginning of conversation" marker so the user has a
              clear end-of-history signal rather than a phantom
              missing button.
            */}
            {hasMore ? (
              <div className="px-6 py-4 flex justify-center">
                <button
                  type="button"
                  onClick={handleLoadOlder}
                  disabled={loadingMore}
                  data-testid="messages-load-more"
                  className="px-3 py-1.5 border border-theme-border-default font-mono text-[11px] uppercase tracking-widest text-theme-text-secondary hover:text-theme-text-primary hover:border-theme-border-emphasis transition-colors duration-150 disabled:opacity-50"
                >
                  {loadingMore ? "Loading…" : "Load older messages"}
                </button>
              </div>
            ) : (
              <div
                data-testid="messages-beginning"
                className="px-6 py-4 flex justify-center font-mono text-[10px] uppercase tracking-widest text-theme-text-tertiary"
              >
                Beginning of conversation
              </div>
            )}
            {items.map((it) =>
              it.kind === "date" ? (
                <DateSeparator key={it.key} ts={it.ts} />
              ) : (
                <Message
                  key={it.key}
                  teamID={teamID}
                  msg={it.msg}
                  users={users}
                  conversations={conversations}
                  avatarPaletteByUserID={avatarPaletteByUserID}
                  isThreadParent={openThreadParentTS === it.msg.ts}
                  groupedContinuation={it.groupedContinuation}
                  isHighlighted={highlightedTs === it.msg.ts}
                  broadcastParent={
                    typeof it.msg.thread_ts === "string"
                    && it.msg.thread_ts !== it.msg.ts
                      ? messagesByTs.get(it.msg.thread_ts)
                      : undefined
                  }
                  onOpenThread={setOpenThreadParent}
                  convID={conversation.id}
                  onGoToParent={onGoToParent}
                />
              ),
            )}
            {/*
              Pass 2.13: when the user landed via search-jump
              (around-fetch path), there may be newer messages
              past the loaded window. "Load newer" symmetric to
              the top-of-list "Load older". Hidden in the default
              browse mode because we always start at the newest.
            */}
            {hasMoreAfter && (
              <div className="px-6 py-4 flex justify-center">
                <button
                  type="button"
                  onClick={handleLoadNewer}
                  disabled={loadingMore}
                  data-testid="messages-load-newer"
                  className="px-3 py-1.5 border border-theme-border-default font-mono text-[11px] uppercase tracking-widest text-theme-text-secondary hover:text-theme-text-primary hover:border-theme-border-emphasis transition-colors duration-150 disabled:opacity-50"
                >
                  {loadingMore ? "Loading…" : "Load newer messages"}
                </button>
              </div>
            )}
          </>
        )}
        </div>
        {/* S3.7: jump-to-latest — floats bottom-right of the body. */}
        {showJumpToLatest && (
          <div className="absolute bottom-loose right-loose z-20">
            <Tooltip content="Jump to latest message" placement="left">
              <button
                type="button"
                onClick={jumpToLatest}
                data-testid="jump-to-latest"
                aria-label="Jump to latest message"
                className="w-9 h-9 flex items-center justify-center rounded-full bg-theme-accent-bg text-theme-accent-text shadow-2xl hover:bg-theme-accent-hover transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-theme-focus-ring"
              >
                <ArrowDown className="w-4 h-4" strokeWidth={2} aria-hidden />
              </button>
            </Tooltip>
          </div>
        )}
        {/* Aurora: the Figma reserves a composer slot at the bottom of
            the pane; this is a READ-ONLY archive, so instead of a fake
            composer we render an honest glass bar in that slot. */}
        <div
          data-testid="message-pane-readonly-bar"
          className="flex-shrink-0 px-4 pb-3 pt-1.5"
        >
          <div className="flex items-center gap-2.5 px-4 py-2.5 rounded-xl bg-white/[0.03] border border-white/[0.07]">
            <Lock
              className="w-3.5 h-3.5 text-theme-text-tertiary flex-shrink-0"
              strokeWidth={1.75}
              aria-hidden
            />
            <span className="font-mono text-[11px] text-theme-text-muted truncate">
              Read-only archive — messages are preserved exactly as they were in Slack.
            </span>
          </div>
        </div>
        </div>
        {/*
          Pass 2.12: the ResizeHandle only renders when (a) the
          thread is open and (b) the parent passed an
          onResizeThreadDrawer (i.e. we're above the 900px viewport
          threshold). The drawer itself renders only when the
          thread is open, regardless of viewport. Below 900px the
          drawer takes the spec default width (BrowseView passes
          420) with no handle.
        */}
        {threadOpen && onResizeThreadDrawer && (
          <ResizeHandle
            orientation="horizontal"
            onResize={onResizeThreadDrawer}
            getCurrentWidth={() => threadDrawerWidth}
            minWidth={THREAD_BOUNDS.min}
            maxWidth={THREAD_BOUNDS.max}
            // Pass 2.12.1: drawer sits to the RIGHT of the handle,
            // so the handle moving right must SHRINK the drawer.
            growsTo="left"
            aria-label="Resize thread drawer"
            data-testid="resize-handle-thread"
          />
        )}
        {threadOpen && (
          <ThreadDrawer
            teamID={teamID}
            convID={conversation.id}
            users={users}
            conversations={conversations}
            avatarPaletteByUserID={avatarPaletteByUserID}
            parentTs={openThreadParentTS}
            fallbackParent={threadParent}
            onClose={() => setOpenThreadParent(null)}
            width={threadDrawerWidth}
          />
        )}
      </div>
    </main>
  );
}
